import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
import {
  IntegrationEntityNames,
  IntegrationStatusToStepForCmp,
  IntegrationStatusToStepForCrm,
  IntegrationsTools,
  defaultGlobalSyncConfig,
  getIntegrationStatusToStepMapping,
} from 'src/features/settings/integrations/constants/integrations';
import {
  getSetConfigureIntegrationData,
  getIntegrationStatus,
  updateIntegrationStatus,
  updateUserMappings,
  getSyncData,
  getTestRunData,
  completeIntegration,
  clearMappings,
  updateGlobalSyncFlag,
  getGlobalSyncFlag,
  getMapValuestableData,
  fetchActiveIntegrations,
} from './integrations-v2.action';
import {
  FieldMappingsMockUpType,
  ITableData,
  IntegrationsToolsType,
  WarningsType,
  WarningDetailsType,
  SyncDirection,
} from 'src/features/settings/integrations/types';
import { GlobalSyncFlagResult } from './types';
import { getSyncDirection } from 'src/features/settings/integrations/utils/integrations';

export type IInitialState = {
  integrationTools: IntegrationsToolsType;
  userMappingtableData: ITableData;
  tableDataLoading?: boolean;
  numericCustomerId?: number;
  autoUpdating?: boolean;
  reauthIntegration: boolean;
  integrationFieldsWithCustomFields: FieldMappingsMockUpType[];
  fieldMappingsTableData: Record<string, ITableData>;
  integrationStatusLoading: boolean;
  testRunData: Record<string, any[]>;
  testRunDataLoading: boolean;
  completeIntegrationLoading: boolean;
  dealStageData: Record<string, string>;
  prospectStagesTableData: ITableData;
  prospectStagesDropdownList: string[];
  mapValuesTableData: ITableData;
  isSyncNowCalledUptoThreshold: boolean;
  globalSyncFlagUpdateLoading?: boolean;
  globalSyncFlag: GlobalSyncFlagResult;
  isGlobleSyncFlagLoaded: boolean;
  globalSyncFlagLoading: boolean;
  totalWarnings: WarningsType;
  warningDetails: WarningDetailsType;
  filterableFieldsByEntity: Record<string, Record<string, { name: string; type: string }>>;
  picklistDropdownValues: Record<string, Record<string, { label: string; value: string }[]>>;
  customFieldsAccount: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
  customFieldsContact: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
  aiSignalFieldsAccount: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
  aiSignalFieldsContact: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
  icpFieldsAccount: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
  icpFieldsContact: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
  hasConnectedCrm: boolean;
  hasConnectedCmp: boolean;
};

const initialTableData = { rowData: [], commonData: {} };

const initialState: IInitialState = {
  integrationTools: IntegrationsTools,
  userMappingtableData: initialTableData,
  fieldMappingsTableData: {
    [IntegrationEntityNames.Companies]: initialTableData,
    [IntegrationEntityNames.Contact]: initialTableData,
    [IntegrationEntityNames.Lead]: initialTableData,
    [IntegrationEntityNames.Deals]: initialTableData,
  },
  prospectStagesTableData: initialTableData,
  tableDataLoading: false,
  autoUpdating: false,
  integrationFieldsWithCustomFields: [],
  integrationStatusLoading: false,
  testRunData: {},
  testRunDataLoading: false,
  completeIntegrationLoading: false,
  dealStageData: {},
  prospectStagesDropdownList: [],
  mapValuesTableData: initialTableData,
  isSyncNowCalledUptoThreshold: true,
  reauthIntegration: false,
  globalSyncFlagUpdateLoading: false,
  globalSyncFlag: defaultGlobalSyncConfig,
  isGlobleSyncFlagLoaded: false,
  globalSyncFlagLoading: true,
  totalWarnings: {
    Account: 0,
    Contact: 0,
    Lead: 0,
    Opportunity: 0,
  },
  warningDetails: {
    Account: {
      Mismatch: [],
      Pending: [],
      ICPScoring: [],
    },
    Contact: {
      Mismatch: [],
      Pending: [],
      ICPScoring: [],
    },
    Lead: {
      Mismatch: [],
      Pending: [],
      ICPScoring: [],
    },
    Opportunity: {
      Mismatch: [],
      Pending: [],
      ICPScoring: [],
    },
  },
  filterableFieldsByEntity: {},
  picklistDropdownValues: {},
  customFieldsAccount: [],
  customFieldsContact: [],
  aiSignalFieldsAccount: [],
  aiSignalFieldsContact: [],
  icpFieldsAccount: [],
  icpFieldsContact: [],
  hasConnectedCrm: false,
  hasConnectedCmp: false,
};

const integrationSlice = createSlice({
  name: 'integrations-v2',
  initialState,
  reducers: {
    setActiveStep: (
      state,
      { payload: { activeStep, toolId } }: PayloadAction<{ toolId: string; activeStep: number }>,
    ) => {
      state.integrationTools[toolId].activeStep = activeStep;
    },

    setIntegrationStatus: (
      state,
      {
        payload: { integrationStatus, toolId },
      }: PayloadAction<{ toolId: string; integrationStatus: string }>,
    ) => {
      state.integrationTools[toolId].integrationStatus = integrationStatus;
    },

    setUserMappingTableData: (
      state,
      { payload }: PayloadAction<{ userMappingtableData: ITableData }>,
    ) => {
      state.userMappingtableData = payload.userMappingtableData;
    },

    setMapValuesTableData: (state, { payload }: PayloadAction<ITableData>) => {
      state.mapValuesTableData = payload;
    },

    setPicklistDropdownValues: (
      state,
      {
        payload,
      }: PayloadAction<{
        entityKey: string;
        fieldName: string;
        options: { label: string; value: string }[];
      }>,
    ) => {
      if (!state.picklistDropdownValues[payload.entityKey]) {
        state.picklistDropdownValues[payload.entityKey] = {};
      }
      state.picklistDropdownValues[payload.entityKey][payload.fieldName] = payload.options;
    },

    setCustomFields: (
      state,
      {
        payload,
      }: PayloadAction<{
        accountFields: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
        contactFields: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
        aiSignalAccountFields: Array<{
          fieldName: string;
          dbColumnName: string;
          fieldType: string;
        }>;
        aiSignalContactFields: Array<{
          fieldName: string;
          dbColumnName: string;
          fieldType: string;
        }>;
        icpAccountFields: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
        icpContactFields: Array<{ fieldName: string; dbColumnName: string; fieldType: string }>;
      }>,
    ) => {
      state.customFieldsAccount = payload.accountFields;
      state.customFieldsContact = payload.contactFields;
      state.aiSignalFieldsAccount = payload.aiSignalAccountFields;
      state.aiSignalFieldsContact = payload.aiSignalContactFields;
      state.icpFieldsAccount = payload.icpAccountFields;
      state.icpFieldsContact = payload.icpContactFields;
    },

    setFieldMappingTableData: (
      state,
      { payload }: PayloadAction<{ data: ITableData; entity: string }>,
    ) => {
      state.fieldMappingsTableData[payload.entity] = payload.data;
    },
    setDealStagesData: (state, { payload }: PayloadAction<Record<string, string>>) => {
      state.dealStageData = payload;
    },
    setProspectStagesTableData: (state, { payload }: PayloadAction<ITableData>) => {
      state.prospectStagesTableData = payload;
    },

    setReAuthIntegration: (state, { payload }: PayloadAction<boolean>) => {
      state.reauthIntegration = payload;
    },

    setIsSyncNowCalledUptoThreshold: (state, { payload }: PayloadAction<boolean>) => {
      state.isSyncNowCalledUptoThreshold = payload;
    },

    setGlobalSyncFlag: (state, { payload }: PayloadAction<GlobalSyncFlagResult>) => {
      state.globalSyncFlag = payload;
    },

    setGlobalSyncFlagLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.globalSyncFlagLoading = payload;
    },

    setAuthConfigFields: (state, action: PayloadAction<{ toolId: string; fields: any[] }>) => {
      const { toolId, fields } = action.payload;
      if (state.integrationTools[toolId]?.authConfig) {
        state.integrationTools[toolId].authConfig!.fields = fields;
      }
    },

    resetMappingsData: (state, { payload }: PayloadAction<string>) => {
      state.userMappingtableData = initialTableData;
      state.fieldMappingsTableData = {
        [IntegrationEntityNames.Companies]: initialTableData,
        [IntegrationEntityNames.Contact]: initialTableData,
        [IntegrationEntityNames.Lead]: initialTableData,
        [IntegrationEntityNames.Deals]: initialTableData,
      };
      state.prospectStagesTableData = initialTableData;
      state.globalSyncFlag = {
        ...defaultGlobalSyncConfig,
        ...(payload === 'hubspot' && {
          providerEntityInboundConfig: {
            [`HUBSPOT_ACCOUNT`]: { create: true, update: true },
            [`HUBSPOT_CONTACTS`]: { create: true, update: true },
            [`HUBSPOT_DEALS`]: { create: true, update: true },
          },
        }),
        ...(payload === 'hubspot' && {
          providerEntityOutboundConfig: {
            [`HUBSPOT_ACCOUNT`]: { create: true, update: true },
            [`HUBSPOT_CONTACTS`]: { create: true, update: true },
            [`HUBSPOT_DEALS`]: { create: true, update: true },
          },
        }),
      };
      state.mapValuesTableData = initialTableData;
    },

    setWarningDetails: (
      state,
      action: PayloadAction<{
        entity: keyof WarningDetailsType;
        status: 'Mismatch' | 'Pending' | 'ICPScoring';
        value: string;
        actionType: 1 | 0;
      }>,
    ) => {
      const { entity, status, value, actionType } = action.payload;
      if (actionType === 1) {
        if (!state.warningDetails[entity][status].some((row) => row === value)) {
          state.warningDetails[entity][status].push(value);
          state.totalWarnings[entity] = (state.totalWarnings[entity] || 0) + 1;
        }
      } else {
        if (state.warningDetails[entity][status].some((row) => row === value)) {
          state.warningDetails[entity][status] = state.warningDetails[entity][status].filter(
            (item) => item !== value,
          );
          state.totalWarnings[entity] = (state.totalWarnings[entity] || 0) - 1;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getIntegrationStatus.fulfilled,
      (state, { payload: { integrationStatus, toolName, integrationType } }) => {
        state.integrationTools[toolName].integrationStatus = integrationStatus;

        // Use config-driven approach with supportedStage
        const supportedStage = IntegrationsTools[toolName]?.supportedStage || [];
        const statusToStepMapping = getIntegrationStatusToStepMapping(supportedStage);

        // Get step from config-driven mapping, fallback to type-based for backward compatibility
        const configStep = statusToStepMapping[integrationStatus];
        const fallbackStep = (
          integrationType === 'CMP' ? IntegrationStatusToStepForCmp : IntegrationStatusToStepForCrm
        )[integrationStatus];

        state.integrationTools[toolName].activeStep = configStep ?? fallbackStep ?? 0;
      },
    );

    // update integration Status
    builder.addCase(updateIntegrationStatus.pending, (state) => {
      state.integrationStatusLoading = true;
    });
    builder.addCase(
      updateIntegrationStatus.fulfilled,
      (state, { payload: { toolName, integrationStatus, integrationType } }) => {
        state.integrationTools[toolName].integrationStatus = integrationStatus;

        // Use config-driven approach with supportedStage
        const supportedStage = IntegrationsTools[toolName]?.supportedStage || [];
        const statusToStepMapping = getIntegrationStatusToStepMapping(supportedStage);

        // Get step from config-driven mapping, fallback to type-based for backward compatibility
        const configStep = statusToStepMapping[integrationStatus];
        const fallbackStep = (
          integrationType === 'CMP' ? IntegrationStatusToStepForCmp : IntegrationStatusToStepForCrm
        )[integrationStatus];

        state.integrationTools[toolName].activeStep = configStep ?? fallbackStep ?? 0;
        state.integrationStatusLoading = false;
      },
    );
    builder.addCase(updateIntegrationStatus.rejected, (state) => {
      state.integrationStatusLoading = false;
    });

    builder.addCase(getSetConfigureIntegrationData.pending, (state) => {
      state.tableDataLoading = true;
    });
    builder.addCase(
      getSetConfigureIntegrationData.fulfilled,
      (
        state,
        {
          payload: {
            userMappingsTableData,
            numericCustomerId,
            fieldMappingsTableData,
            dealStageData,
            prospectStagesDropdownList,
            prospectStagesTableData,
            globalSyncFlag,
            filterableFieldsByEntity,
          },
        },
      ) => {
        state.userMappingtableData = userMappingsTableData;
        state.tableDataLoading = false;
        state.numericCustomerId = numericCustomerId;
        state.fieldMappingsTableData = fieldMappingsTableData;
        state.dealStageData = dealStageData;
        state.prospectStagesDropdownList = prospectStagesDropdownList;
        state.prospectStagesTableData = prospectStagesTableData;
        // Update globalSyncFlag if provided (for integrations with custom sync direction configs)
        if (globalSyncFlag) {
          state.globalSyncFlag = globalSyncFlag;
        }
        // Store processed filterable fields for condition builder dropdown options
        if (filterableFieldsByEntity) {
          state.filterableFieldsByEntity = filterableFieldsByEntity;
        }
      },
    );
    builder.addCase(getSetConfigureIntegrationData.rejected, (state) => {
      state.tableDataLoading = false;
    });

    builder.addCase(getMapValuestableData.fulfilled, (state, { payload }) => {
      state.mapValuesTableData = payload;
    });

    builder.addCase(updateUserMappings.pending, (state) => {
      state.autoUpdating = true;
    });
    builder.addCase(updateUserMappings.fulfilled, (state) => {
      state.autoUpdating = false;
    });
    builder.addCase(updateUserMappings.rejected, (state) => {
      state.autoUpdating = false;
    });

    // sync data
    builder.addCase(getSyncData.pending, (state, { meta }) => {
      const integrationKey = Object.keys(IntegrationsTools).find(
        (key) => IntegrationsTools[key].providerName === meta.arg.providerName,
      );
      const toolKey = integrationKey || meta.arg.providerName.toLowerCase();
      if (state.integrationTools[toolKey]) {
        state.integrationTools[toolKey].syncDataLoading = true;
      }
    });
    builder.addCase(
      getSyncData.fulfilled,
      (state, { payload: { toolId, data, totalSyncData, totalPages } }) => {
        const tool = state.integrationTools[toolId];
        if (!tool) return;

        tool.syncDataLoading = false;

        // Safely update lastSyncedAt
        const currentLastSyncedAt = tool.lastSyncedAt;
        const latestDataTimestamp = data?.[0]?.updated_timestamp;

        if (latestDataTimestamp) {
          tool.lastSyncedAt =
            currentLastSyncedAt && new Date(currentLastSyncedAt) > new Date(latestDataTimestamp)
              ? currentLastSyncedAt
              : latestDataTimestamp;
        }

        // Safely update lastAutoSyncAt
        const autoSyncElement = data?.find(function (element) {
          if (!element?.updated_timestamp) return false;

          const lastSyncedTime = dayjs(element.updated_timestamp);
          const minutes = lastSyncedTime.minute();
          if (minutes % 10 === 0 || minutes % 10 === 5) {
            const currentLastAutoSyncAt = tool.lastAutoSyncAt;
            if (
              !currentLastAutoSyncAt ||
              new Date(element.updated_timestamp) > new Date(currentLastAutoSyncAt)
            ) {
              return element;
            }
          }
          return false;
        });

        if (autoSyncElement?.updated_timestamp) {
          tool.lastAutoSyncAt = autoSyncElement.updated_timestamp;
        }

        tool.syncData = data;
        tool.totalSyncData = totalSyncData;
        tool.totalPages = totalPages;
      },
    );
    builder.addCase(getSyncData.rejected, (state, { meta }) => {
      const integrationKey = Object.keys(IntegrationsTools).find(
        (key) => IntegrationsTools[key].providerName === meta.arg.providerName,
      );
      const tool = state.integrationTools[integrationKey || meta.arg.providerName.toLowerCase()];
      if (tool) {
        tool.syncDataLoading = false;
      }
    });

    // get Test data
    builder.addCase(getTestRunData.pending, (state) => {
      state.testRunDataLoading = true;
    });
    builder.addCase(getTestRunData.fulfilled, (state, { payload }) => {
      state.testRunDataLoading = false;
      state.testRunData = payload;
    });
    builder.addCase(getTestRunData.rejected, (state) => {
      state.testRunDataLoading = false;
    });

    // complete integratio
    builder.addCase(completeIntegration.pending, (state) => {
      state.completeIntegrationLoading = true;
    });
    builder.addCase(completeIntegration.fulfilled, (state) => {
      state.completeIntegrationLoading = false;
    });
    builder.addCase(completeIntegration.rejected, (state) => {
      state.completeIntegrationLoading = false;
    });

    builder.addCase(clearMappings.fulfilled, (state) => {
      state.userMappingtableData = initialTableData;
      state.fieldMappingsTableData = {
        [IntegrationEntityNames.Companies]: initialTableData,
        [IntegrationEntityNames.Contact]: initialTableData,
        [IntegrationEntityNames.Lead]: initialTableData,
        [IntegrationEntityNames.Deals]: initialTableData,
      };
      state.prospectStagesTableData = initialTableData;
      state.globalSyncFlag = defaultGlobalSyncConfig;
      state.mapValuesTableData = initialTableData;
    });

    // get global sync flag
    builder.addCase(getGlobalSyncFlag.pending, (state) => {
      state.globalSyncFlagLoading = true;
    });
    builder.addCase(
      getGlobalSyncFlag.fulfilled,
      (
        state,
        {
          payload: {
            fromSprouts,
            historicInterval,
            intoSprouts,
            isLeadEnabled,
            dealEnabled,
            isManualSyncEnabled,
            syncNewSproutsContactsTo,
            isHistoricFetchEnabled,
            syncFrequency,
            providerEntityInboundConfig,
            providerEntityOutboundConfig,
            providerName,
            isEmailValidationRequire,
            callEnabled,
            meetingEnabled,
            taskEnabled,
            emailEnabled,
            emailAsTaskEnabled,
          },
        },
      ) => {
        if (fromSprouts !== null && intoSprouts !== null) {
          state.globalSyncFlag = {
            fromSprouts,
            historicInterval,
            intoSprouts,
            isLeadEnabled,
            dealEnabled,
            isManualSyncEnabled,
            syncNewSproutsContactsTo,
            isHistoricFetchEnabled,
            syncFrequency,
            providerEntityInboundConfig,
            providerEntityOutboundConfig,
            isEmailValidationRequire,
            callEnabled,
            meetingEnabled,
            taskEnabled,
            emailEnabled,
            emailAsTaskEnabled,
          };
        } else {
          const syncDirection = getSyncDirection(providerName);

          state.globalSyncFlag = {
            ...defaultGlobalSyncConfig,
            // Preserve isLeadEnabled from API if explicitly set (e.g. BIDIRECTIONAL integrations
            // where fromSprouts/intoSprouts haven't been configured yet)
            ...(isLeadEnabled !== undefined && { isLeadEnabled }),
            ...(dealEnabled !== undefined && { dealEnabled }),
            // For OUTBOUND_ONLY integrations, enable lead by default
            ...(syncDirection === SyncDirection.OUTBOUND_ONLY && {
              isLeadEnabled: true,
              dealEnabled: true,
              syncNewSproutsContactsTo: 'Lead',
              intoSprouts: false,
              fromSprouts: true,
              isHistoricFetchEnabled: false,
            }),
            // For INBOUND_ONLY integrations (Gong), disable outbound sync and enable inbound sync
            ...(syncDirection === SyncDirection.INBOUND_ONLY && {
              intoSprouts: true,
              fromSprouts: false,
              isLeadEnabled: false,
              dealEnabled: true,
            }),
            ...(providerName === 'HUBSPOT' && {
              providerEntityInboundConfig: {
                [`${providerName}_ACCOUNT`]: { create: true, update: true },
                [`${providerName}_CONTACTS`]: { create: true, update: true },
                [`${providerName}_DEALS`]: { create: true, update: true },
              },
            }),
            ...(providerName === 'HUBSPOT' && {
              providerEntityOutboundConfig: {
                [`${providerName}_ACCOUNT`]: { create: true, update: true },
                [`${providerName}_CONTACTS`]: { create: true, update: true },
                [`${providerName}_DEALS`]: { create: true, update: true },
              },
            }),
          };
        }
        state.isGlobleSyncFlagLoaded = true;
        state.globalSyncFlagLoading = false;
      },
    );
    builder.addCase(getGlobalSyncFlag.rejected, (state) => {
      // Set default config when API call fails
      state.globalSyncFlag = defaultGlobalSyncConfig;
      state.isGlobleSyncFlagLoaded = true;
      state.globalSyncFlagLoading = false;
    });

    // update global sync flag
    builder.addCase(updateGlobalSyncFlag.pending, (state) => {
      state.globalSyncFlagUpdateLoading = true;
    });
    builder.addCase(updateGlobalSyncFlag.fulfilled, (state) => {
      // No-op for globalSyncFlag data:
      // UI already updates local state via setGlobalSyncFlag before autosave.
      // Replacing globalSyncFlag here retriggers autosave effect and can cause save loops.
      state.globalSyncFlagUpdateLoading = false;
    });
    builder.addCase(updateGlobalSyncFlag.rejected, (state) => {
      state.globalSyncFlagUpdateLoading = false;
    });

    builder.addCase(fetchActiveIntegrations.fulfilled, (state, { payload }) => {
      state.hasConnectedCrm = payload.hasConnectedCrm;
      state.hasConnectedCmp = payload.hasConnectedCmp;
    });
  },
});

export const {
  setActiveStep,
  setIntegrationStatus,
  setUserMappingTableData,
  setReAuthIntegration,
  setFieldMappingTableData,
  setMapValuesTableData,
  setPicklistDropdownValues,
  setCustomFields,
  setDealStagesData,
  setProspectStagesTableData,
  setIsSyncNowCalledUptoThreshold,
  setGlobalSyncFlag,
  resetMappingsData,
  setWarningDetails,
  setGlobalSyncFlagLoading,
  setAuthConfigFields,
} = integrationSlice.actions;

export default integrationSlice.reducer;
